import { assertHasIdProperty, type IEntityMaskData } from '~/types/models'

export interface ITestBasedOnField {
  inputFields: IEntityMaskData[]
  sourceFieldName: string
}

export interface ITestBasedOnSelectItemId {
  inputFields: IEntityMaskData[]
  sourceFieldName: string
  id: string | number
}

export interface ITestBasedOnFunction {
  inputFields: IEntityMaskData[]
  sourceFieldName: string
  testFunction: Function
}

export function isNullFieldValue(obj: ITestBasedOnField) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  return f.value === null || f.value === ''
}

export function isUndefinedFieldValue(obj: ITestBasedOnField) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  return f.value === undefined
}

export function isNullFieldValueOrEmptyStringOrFieldIsDisabled(
  obj: ITestBasedOnField
) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  return f.value === null || f.value === '' || f.disabled
}

export function isFalseyFieldValue(obj: ITestBasedOnField) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  const retVal = !f.value
  console.debug(`isFalseyFieldValue returning`, retVal)
  return retVal
}

export function isFalseyFieldStartValue(obj: ITestBasedOnField) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  const retVal = !f.start
  console.debug(`isFalseyFieldStartValue returning`, retVal)
  return retVal
}

export function hasSpecficSelectItemValue(obj: ITestBasedOnSelectItemId) {
  const { inputFields, sourceFieldName, id } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData
  // Assert only what this function USES. It compares ids and never reads
  // `label`, but assertIsASelectItem additionally requires one — and an
  // edit-dialog value comes straight from entity data via
  // setInputFieldsStartAndValue, unenriched. Under the MSW mocks those nested
  // select values omit `label` (country carries `name`, industry `descr`, ...),
  // so this threw on save for any Company with an address country:
  //   hasSpecficSelectItemValue -> isDomesticAddress -> buildFieldValidations
  //   -> trySave.
  // Live data does carry `label`, so this was mock-only — but fatal there.
  // Display is unaffected either way: selectItemTitle() falls back to `name`.
  // Same shape as the country field's own runOnDefaultSet, which already
  // id-asserts this value rather than select-item-asserting it.
  assertHasIdProperty(hasSpecficSelectItemValue.name, f.value)
  return f.value.id === id
}

export function hasEmptyItemsArray(obj: ITestBasedOnField) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData

  if (!f) {
    return true
  }
  return !f.items || f.items.length < 1
}

export function isFalseyFieldNotArrayOrValueOfTypeArrayWithLengthOne(
  obj: ITestBasedOnField
) {
  const { inputFields, sourceFieldName } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData

  if (!f || !f.value || !Array.isArray(f.value)) {
    return true
  }

  return f.value.length === 1
}

export function doesFailTestFunctionForField(obj: ITestBasedOnFunction) {
  const { inputFields, sourceFieldName, testFunction } = obj
  const f = inputFields.find(
    (ifield) => ifield.name == sourceFieldName
  ) as IEntityMaskData

  if (!f || !f.value) {
    return true
  }

  return !testFunction(f.value)
}
